/**
 * BulkImportDock — a persistent, global status pill for an in-flight bulk
 * resume import. Rendered once in the Layout, so a running import is
 * visible on EVERY page (previously, navigating off the import page or
 * choosing "Run in background" left no trace that anything was happening).
 *
 * Aesthetic: the same instrument-panel language as IngestProgressModal —
 * IBM Plex Mono, navy/teal, a pulsing live dot. A compact console readout,
 * not a generic toast.
 *
 * It reads the active batch from the shared store (survives navigation +
 * refresh), polls the status endpoint, and:
 *   - in progress -> "Resume import · 60% · 3/5" (click to open the page)
 *   - poll 401    -> "Session expired" (click to re-auth on the page)
 *   - complete    -> "Import complete · N sourced" with a dismiss
 * It hides itself on the import page, which already shows the full modal.
 */
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { get, ApiResponseError } from '../lib/api';
import { keys } from '../lib/query';
import {
  useActiveBulkImport,
  clearActiveBulkImport,
} from '../lib/bulkImportStore';

interface IngestJobStatus {
  status: string;
}
interface IngestBatchStatusResponse {
  total: number;
  by_status: Record<string, number>;
  jobs: IngestJobStatus[];
}

const TERMINAL = new Set(['materialised', 'llm_failed', 'archived']);
const IMPORT_PATH = '/dashboard/candidates/bulk-import';

export function BulkImportDock() {
  const active = useActiveBulkImport();
  const location = useLocation();
  const navigate = useNavigate();

  const onImportPage = location.pathname.startsWith(IMPORT_PATH);

  const statusQuery = useQuery({
    queryKey: active
      ? keys.sourced.batches(active.batchId)
      : ['sourced', 'batches', 'idle'],
    queryFn: () =>
      get<IngestBatchStatusResponse>(
        `/admin/sourced/batches/${active!.batchId}/status`,
      ),
    enabled: !!active && !onImportPage,
    refetchInterval: (q) => {
      const d = q.state.data as IngestBatchStatusResponse | undefined;
      if (!d) return 3000;
      return d.jobs.every((j) => TERMINAL.has(j.status)) ? false : 3000;
    },
  });

  // The import page owns the full UI; don't double up.
  if (!active || onImportPage) return null;

  const data = statusQuery.data;
  const authError =
    statusQuery.isError &&
    statusQuery.error instanceof ApiResponseError &&
    statusQuery.error.status === 401;

  const total = data?.total ?? active.filesTotal;
  const done =
    (data?.by_status.materialised ?? 0) + (data?.by_status.llm_failed ?? 0);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const allDone = !!data && total > 0 && done >= total;
  const failed = data?.by_status.llm_failed ?? 0;

  // ── State-specific content ───────────────────────────────────────────
  let dotClass = 'bg-[var(--color-primary)] animate-pulse';
  let label = 'Resume import';
  let detail = `${pct}% · ${done}/${total}`;

  if (authError) {
    dotClass = 'bg-[var(--color-warn)]';
    label = 'Resume import';
    detail = 'session expired — log in';
  } else if (allDone) {
    dotClass = 'bg-[var(--color-success)]';
    label = 'Import complete';
    detail = failed
      ? `${done - failed} sourced · ${failed} failed`
      : `${done} sourced`;
  }

  return (
    <div className="fixed bottom-6 right-6 z-[var(--z-overlay,40)] prism-enter">
      <div className="flex items-center gap-3 rounded-[6px] border border-[var(--color-rule)] bg-[var(--color-surface)] py-2.5 pl-3.5 pr-2.5 shadow-[0_16px_40px_-16px_rgba(25,54,97,0.35)]">
        <span
          className={`inline-block h-[8px] w-[8px] shrink-0 rounded-full ${dotClass}`}
          aria-hidden="true"
        />
        <button
          type="button"
          onClick={() => navigate(IMPORT_PATH)}
          className="text-left"
          aria-label="Open bulk import progress"
        >
          <span className="block font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--color-ink-soft)]">
            {label}
          </span>
          <span className="block font-mono text-[12.5px] font-semibold tabular-nums text-[var(--color-ink)]">
            {detail}
          </span>
        </button>

        {/* Thin progress rail (hidden in the auth-error state) */}
        {!authError && (
          <div className="hidden h-[5px] w-20 overflow-hidden rounded-full bg-[var(--color-surface-muted)] sm:block">
            <div
              className={`h-full rounded-full ${allDone ? 'bg-[var(--color-success)]' : 'bg-[var(--color-primary)]'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}

        {/* Dismiss only once terminal — never lets you hide an active run */}
        {allDone && (
          <button
            type="button"
            onClick={() => clearActiveBulkImport()}
            aria-label="Dismiss"
            className="ml-1 flex h-6 w-6 items-center justify-center rounded-[4px] text-[var(--color-ink-soft)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-ink)]"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}
